import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./chartFullscreenShell.css";

const GraphV2FullscreenContext = createContext(null);

/** @returns {boolean} */
export function useGraphV2Fullscreen() {
  const ctx = useContext(GraphV2FullscreenContext);
  return Boolean(ctx?.isFullscreen);
}

/** Legend row + other in-chart controls */
export function useGraphV2FullscreenActions() {
  return useContext(GraphV2FullscreenContext);
}

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    null
  );
}

function requestElemFullscreen(el) {
  if (!el) return Promise.reject(new Error("no element"));
  if (el.requestFullscreen) return el.requestFullscreen();
  if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
    return Promise.resolve();
  }
  if (el.msRequestFullscreen) return el.msRequestFullscreen();
  return Promise.reject(new Error("fullscreen not supported"));
}

function exitDocumentFullscreen() {
  if (document.exitFullscreen) return document.exitFullscreen();
  if (document.webkitExitFullscreen) return document.webkitExitFullscreen();
  if (document.msExitFullscreen) return document.msExitFullscreen();
  return Promise.resolve();
}

/** Heroicons outline "arrows pointing out" — inline SVG so the icon always paints. */
export function IconEnterFullscreen() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 20.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Heroicons outline "arrows pointing in" */
export function IconExitFullscreen() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0 4.5l5.25 5.25"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const FULLSCREEN_ICON_BTN_BASE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  padding: "0",
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  backgroundColor: "#fff",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
};

/** Same 32×32 fullscreen control as the third slot in LegendControls (inline SVG). */
export function FullscreenIconOnlyButton() {
  const fs = useGraphV2FullscreenActions();
  if (!fs?.toggleFullscreen) return null;
  return (
    <button
      type="button"
      onClick={() => void fs.toggleFullscreen()}
      title={fs.isFullscreen ? "Exit full screen (Esc)" : "Full screen graph"}
      aria-label={fs.isFullscreen ? "Exit full screen graph" : "Open graph in full screen"}
      aria-expanded={fs.isFullscreen}
      style={{
        ...FULLSCREEN_ICON_BTN_BASE,
        color: "#0f172a",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#f8fafc";
        e.currentTarget.style.borderColor = "#94a3b8";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "#fff";
        e.currentTarget.style.borderColor = "#e5e7eb";
      }}
    >
      {fs.isFullscreen ? <IconExitFullscreen /> : <IconEnterFullscreen />}
    </button>
  );
}

/**
 * Top row for graphs with no legend toolbar (e.g. standard-vs-actual): right-aligned
 * fullscreen icon; in native fullscreen, graph title on the left (matches legend charts).
 * @param {{ endExtras?: React.ReactNode }} [props] — optional controls rendered
 *   immediately before the fullscreen button (right cluster).
 */
export function GraphV2FullscreenInlineRow({ endExtras = null } = {}) {
  const fs = useGraphV2FullscreenActions();
  if (!fs?.toggleFullscreen && !endExtras) return null;
  return (
    <div
      data-html2canvas-ignore="true"
      className="graph-v2-fs-inline-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "10px",
        width: "100%",
      }}
    >
      {fs?.isFullscreen && fs.graphTitle ? (
        <span
          title={fs.graphTitle}
          style={{
            minWidth: 0,
            flex: "1 1 auto",
            fontSize: "13px",
            fontWeight: 600,
            color: "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            paddingRight: "8px",
          }}
        >
          {fs.graphTitle}
        </span>
      ) : (
        <span style={{ flex: "1 1 auto", minWidth: 0 }} aria-hidden />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
          marginLeft: "auto",
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {endExtras}
        {fs?.toggleFullscreen ? <FullscreenIconOnlyButton /> : null}
      </div>
    </div>
  );
}

/** Default plot body heights for pond-stat / pond-detail charts (non-fullscreen). */
export const POND_STAT_MOBILE_HEIGHT = 460;
export const POND_STAT_DESKTOP_HEIGHT = 560;

/**
 * Plot height for V2 charts: grows with viewport when full screen is active.
 * @param {object} opts
 * @param {boolean} opts.isMobile
 * @param {number} opts.mobileDefault
 * @param {number} opts.desktopDefault
 * @param {number} [opts.legendExtra]
 * @param {number} [opts.fullscreenReserved] — viewport minus this value (toolbar, padding, axes)
 */
export function useGraphV2PlotHeight({
  isMobile,
  mobileDefault,
  desktopDefault,
  legendExtra = 0,
  fullscreenReserved,
}) {
  const isFullscreen = useGraphV2Fullscreen();
  const [vh, setVh] = useState(
    () => (typeof window !== "undefined" ? window.innerHeight : 800)
  );

  useEffect(() => {
    if (!isFullscreen) return undefined;
    const update = () => setVh(window.innerHeight);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isFullscreen]);

  const base = isMobile ? mobileDefault : desktopDefault;
  const fullscreenTopPad = isMobile ? 16 : 24;
  const reserved =
    fullscreenReserved != null
      ? fullscreenReserved + fullscreenTopPad
      : isMobile
        ? 200 + fullscreenTopPad
        : 175 + fullscreenTopPad;
  const plotBody = isFullscreen ? Math.max(base, vh - reserved) : base;
  return plotBody + legendExtra;
}

export default function ChartFullscreenShell({
  title,
  children,
  /** When true, only the in-chart legend row shows fullscreen (no duplicate top control). */
  hideExternalToolbar = false,
}) {
  const rootRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  const syncFromDocument = useCallback(() => {
    const el = rootRef.current;
    setIsFs(Boolean(el && getFullscreenElement() === el));
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      syncFromDocument();
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("mozfullscreenchange", onFsChange);
    document.addEventListener("MSFullscreenChange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("mozfullscreenchange", onFsChange);
      document.removeEventListener("MSFullscreenChange", onFsChange);
    };
  }, [syncFromDocument]);

  const toggle = useCallback(async () => {
    const el = rootRef.current;
    if (!el) return;
    if (getFullscreenElement() === el) {
      try {
        await exitDocumentFullscreen();
      } catch {
        /* ignore */
      }
      setIsFs(false);
      return;
    }
    try {
      await requestElemFullscreen(el);
      syncFromDocument();
    } catch {
      /* iOS / blocked: stay inline */
      setIsFs(false);
    }
  }, [syncFromDocument]);

  const ctxValue = useMemo(
    () => ({ isFullscreen: isFs, toggleFullscreen: toggle, graphTitle: title }),
    [isFs, toggle, title]
  );

  return (
    <GraphV2FullscreenContext.Provider value={ctxValue}>
      <div ref={rootRef} className="graph-v2-fs-root relative w-full">
        {!hideExternalToolbar && (
          <div
            className={
              isFs
                ? "graph-v2-fs-toolbar flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 py-2 md:px-4"
                : "graph-v2-fs-toolbar mb-2 flex justify-end"
            }
          >
            {isFs && (
              <span
                className="min-w-0 flex-1 truncate pr-2 text-sm font-semibold text-gray-900 md:text-base"
                title={title}
              >
                {title}
              </span>
            )}
            <button
              type="button"
              onClick={() => void toggle()}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium shadow-sm transition hover:bg-gray-50 md:text-sm"
              style={{ color: "#0f172a" }}
              aria-expanded={isFs}
              title={isFs ? "Exit full screen (Esc)" : "Full screen graph"}
              aria-label={isFs ? "Exit full screen graph" : "Open graph in full screen"}
            >
              {isFs ? <IconExitFullscreen /> : <IconEnterFullscreen />}
              <span className="hidden sm:inline">
                {isFs ? "Exit full screen" : "Full screen"}
              </span>
            </button>
          </div>
        )}
        <div
          className={
            isFs
              ? "graph-v2-fs-body w-full px-2 pb-2 pt-0 md:px-4 md:pb-4"
              : "graph-v2-fs-body w-full"
          }
        >
          {children}
        </div>
      </div>
    </GraphV2FullscreenContext.Provider>
  );
}
